import { Box, BoxProps, SystemStyleObject, useMergeRefs } from "@chakra-ui/react";
import { ReactNode, Ref, useCallback, useEffect, useState } from "react";

const END_TOLERANCE_PX = 1;

export interface HorizontalOverflow {
  hiddenLeft: boolean;
  hiddenRight: boolean;
}

export const measureHorizontalOverflow = (scroller: HTMLElement): HorizontalOverflow => {
  const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
  return {
    hiddenLeft: scroller.scrollLeft > END_TOLERANCE_PX,
    hiddenRight: scroller.scrollLeft < maxScrollLeft - END_TOLERANCE_PX,
  };
};

export const useHorizontalOverflow = () => {
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const [overflow, setOverflow] = useState<HorizontalOverflow>({ hiddenLeft: false, hiddenRight: false });

  const measure = useCallback(() => {
    if (!scroller) return;
    const next = measureHorizontalOverflow(scroller);
    setOverflow((current) =>
      current.hiddenLeft === next.hiddenLeft && current.hiddenRight === next.hiddenRight ? current : next,
    );
  }, [scroller]);

  useEffect(() => {
    if (!scroller) return;
    measure();
    scroller.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    Array.from(scroller.children).forEach((child) => observer.observe(child));
    return () => {
      scroller.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [scroller, measure]);

  return { ref: setScroller, ...overflow };
};

export const pinnedColumnEdge = (pinnedTo: "left" | "right"): SystemStyleObject => {
  const border = pinnedTo === "right" ? "borderLeft" : "borderRight";
  return {
    [`${border}Width`]: "1px",
    [`${border}Style`]: "solid",
    [`${border}Color`]: "transparent",
    transition: "border-color 0.15s",
    [`[data-hidden-${pinnedTo}] &`]: { [`${border}Color`]: "smPinnedColumnEdge" },
  };
};

const EdgeShadow = ({ side, isVisible }: { side: "left" | "right"; isVisible: boolean }) => (
  <Box
    data-testid={`scroll-edge-${side}`}
    aria-hidden
    position="absolute"
    top="0"
    bottom="0"
    {...{ [side]: 0 }}
    width="8"
    pointerEvents="none"
    zIndex="1"
    backgroundImage={`linear-gradient(to ${side}, transparent, var(--chakra-colors-smScrollEdgeShadow))`}
    opacity={isVisible ? 1 : 0}
    transition="opacity 0.15s"
  />
);

export const HorizontalScrollBox = ({
  children,
  scrollerRef,
  ...frameProps
}: { children: ReactNode; scrollerRef?: Ref<HTMLElement> } & BoxProps) => {
  const { ref, hiddenLeft, hiddenRight } = useHorizontalOverflow();
  const scrollerRefs = useMergeRefs<HTMLElement | null>(ref, scrollerRef);

  return (
    <Box
      position="relative"
      overflow="hidden"
      data-hidden-left={hiddenLeft || undefined}
      data-hidden-right={hiddenRight || undefined}
      {...frameProps}
    >
      <EdgeShadow side="left" isVisible={hiddenLeft} />
      <Box ref={scrollerRefs} overflowX="auto">
        {children}
      </Box>
      <EdgeShadow side="right" isVisible={hiddenRight} />
    </Box>
  );
};
